import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/InstallAppButton";
import api, { apiError } from "@/lib/api";
import { t } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bootstrapAvailable, setBootstrapAvailable] = useState(false);
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [boot, setBoot] = useState({ name: "", email: "", password: "", confirm: "" });

  useEffect(() => {
    api.get("/auth/bootstrap/status").then((r) => setBootstrapAvailable(!!r.data.available)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const data = await login(email, password);
      navigate(["enterprise_admin", "enterprise_member", "auditor"].includes(data.role) ? "/enterprise" : "/");
    } catch (err) {
      setError(apiError(err));
    } finally { setBusy(false); }
  };

  const submitBootstrap = async (e) => {
    e.preventDefault();
    if (boot.password !== boot.confirm) { setError("Passwords do not match"); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/bootstrap", { name: boot.name, email: boot.email, password: boot.password });
      await login(boot.email, boot.password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/assets/hussar-logo.png" alt="Hussar Treasury" className="w-64 mx-auto" data-testid="login-logo" />
        </div>
        <form onSubmit={submit} className="panel p-6 space-y-4" data-testid="login-form">
          <div className="space-y-1.5">
            <Label className="metric-label" htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" data-testid="login-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} autoComplete="email"
              className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
          </div>
          <div className="space-y-1.5">
            <Label className="metric-label" htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" data-testid="login-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
              className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
          </div>
          {error && !showBootstrap && <div className="text-red-400 text-xs font-mono border border-red-800/60 bg-red-950/30 rounded-sm px-3 py-2" data-testid="login-error">{error}</div>}
          <Button data-testid="login-form-submit-button" type="submit" disabled={busy}
            className="w-full bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold tracking-wide">
            {busy ? t("action.signingIn") : t("action.signIn")}
          </Button>
          <p className="text-[10px] text-slate-400 text-center">{t("auth.authorizedOnly")}</p>
        </form>

        {bootstrapAvailable && (
          <div className="mt-4" data-testid="bootstrap-section">
            {!showBootstrap ? (
              <button type="button" data-testid="bootstrap-toggle" onClick={() => { setShowBootstrap(true); setError(""); }}
                className="w-full flex items-center justify-center gap-2 text-[11px] tracking-widest uppercase text-slate-500 hover:text-[#D4AF37] border border-dashed border-[#2D323E] hover:border-[#D4AF37]/50 rounded-sm py-2.5 transition-colors">
                <ShieldCheck className="w-3.5 h-3.5" /> {t("auth.bootstrapToggle")}
              </button>
            ) : (
              <form onSubmit={submitBootstrap} className="panel p-6 space-y-4 border-[#D4AF37]/40" data-testid="bootstrap-form">
                <div className="flex items-center gap-2 text-[#D4AF37]">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-widest uppercase">{t("auth.bootstrapTitle")}</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed">{t("auth.bootstrapDesc")}</p>
                <div className="space-y-1.5">
                  <Label className="metric-label" htmlFor="boot-name">{t("auth.name")}</Label>
                  <Input id="boot-name" data-testid="bootstrap-name-input" value={boot.name}
                    onChange={(e) => setBoot({ ...boot, name: e.target.value })} autoComplete="name"
                    className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="metric-label" htmlFor="boot-email">{t("auth.email")}</Label>
                  <Input id="boot-email" data-testid="bootstrap-email-input" type="email" required value={boot.email}
                    onChange={(e) => setBoot({ ...boot, email: e.target.value })} autoComplete="email"
                    className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="metric-label" htmlFor="boot-password">{t("auth.password")}</Label>
                  <Input id="boot-password" data-testid="bootstrap-password-input" type="password" required value={boot.password}
                    onChange={(e) => setBoot({ ...boot, password: e.target.value })} autoComplete="new-password"
                    className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="metric-label" htmlFor="boot-confirm">{t("auth.confirmPassword")}</Label>
                  <Input id="boot-confirm" data-testid="bootstrap-confirm-input" type="password" required value={boot.confirm}
                    onChange={(e) => setBoot({ ...boot, confirm: e.target.value })} autoComplete="new-password"
                    className="bg-[#121316] border-[#2D323E] text-slate-100 focus-visible:ring-[#D4AF37]" />
                </div>
                <p className="text-[10px] text-slate-600">{t("auth.passwordRules")}</p>
                {error && <div className="text-red-400 text-xs font-mono border border-red-800/60 bg-red-950/30 rounded-sm px-3 py-2" data-testid="bootstrap-error">{error}</div>}
                <Button data-testid="bootstrap-submit-button" type="submit" disabled={busy}
                  className="w-full bg-[#D4AF37] hover:bg-[#C5A059] text-[#0B0C0E] font-bold tracking-wide">
                  {busy ? t("action.bootstrapping") : t("action.bootstrap")}
                </Button>
              </form>
            )}
          </div>
        )}

        {!bootstrapAvailable && (
          <div className="mt-5 flex justify-center" data-testid="login-install-section">
            <InstallAppButton />
          </div>
        )}
      </div>
    </div>
  );
}
